import React, { memo, useCallback } from 'react';

import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useFormContext from '../hooks/useFormContext';
import LoanActionAiProcessSelect from '../LoanActionAiProcessSelect';
import { wrapperClassname } from '../LoanActionItem.constants';
import LoanActionTagSelect from '../LoanActionTagSelect';

import {
  RERUN_ON_TRIGGER_LABEL,
  RUN_AI_PROCESS_TAG_FIELDS,
  RUN_AI_PROCESS_TAGS_TITLE,
} from './LoanActionRunAiProcessRow.constants';
import { LoanActionRunAiProcessRowTestIds } from './LoanActionRunAiProcessRow.testIds';

const LoanActionRunAiProcessRow = ({
  actionIndex,
  rootPath,
  tagsOptions,
  isDisabled,
  isEditAllowed,
  isAutomatedActions = false,
}) => {
  const { form, validationErrors, setInForm, validateField } = useFormContext();

  const runAiProcess = _.get(form, rootPath) ?? {};
  const processIdError = _.get(validationErrors, `${rootPath}.processId`) ?? null;
  const isFieldDisabled = isDisabled || !isEditAllowed;

  const onProcessChange = useCallback(
    processId => {
      setInForm(`${rootPath}.processId`, processId);
      validateField?.(`${rootPath}.processId`, processId);
    },
    [rootPath, setInForm, validateField],
  );

  const onRerunChange = useCallback(
    event => setInForm(`${rootPath}.rerunOnTrigger`, event.target.checked),
    [rootPath, setInForm],
  );

  return (
    <Box className={wrapperClassname}>
      <LoanActionAiProcessSelect
        dataTestId={LoanActionRunAiProcessRowTestIds.getProcessSelectId(actionIndex)}
        error={processIdError}
        isDisabled={isFieldDisabled}
        name={`${rootPath}.processId`}
        processId={runAiProcess.processId ?? null}
        onChange={onProcessChange}
      />
      <Typography color="text.secondary" variant="medium14">
        {RUN_AI_PROCESS_TAGS_TITLE}
      </Typography>
      {RUN_AI_PROCESS_TAG_FIELDS.map(({ key, testIdKey, label }) => (
        <LoanActionTagSelect
          dataTestId={LoanActionRunAiProcessRowTestIds.getTagSelectId(actionIndex, testIdKey)}
          isDisabled={isFieldDisabled}
          key={key}
          label={label}
          name={`${rootPath}.${key}`}
          tagId={runAiProcess[key] ?? null}
          tagsOptions={tagsOptions}
          onChange={setInForm}
        />
      ))}
      {isAutomatedActions && (
        <FormControlLabel
          control={
            <Switch
              checked={!!runAiProcess.rerunOnTrigger}
              data-testid={LoanActionRunAiProcessRowTestIds.getRerunSwitchId(actionIndex)}
              disabled={isFieldDisabled}
              name={`${rootPath}.rerunOnTrigger`}
              onChange={onRerunChange}
            />
          }
          label={RERUN_ON_TRIGGER_LABEL}
        />
      )}
    </Box>
  );
};

LoanActionRunAiProcessRow.propTypes = {
  actionIndex: PropTypes.number.isRequired,
  rootPath: PropTypes.string.isRequired,
  tagsOptions: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isAutomatedActions: PropTypes.bool,
};

export default memo(LoanActionRunAiProcessRow);
